import React, { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Skeleton } from './components/Skeleton'
import { Toaster } from './components/Toaster'
import { CommandPalette } from './components/CommandPalette'
import { Login } from './pages/Login'
import { useAuth } from './lib/hooks'
import { AuthProvider } from './lib/AuthProvider'

/**
 * Lazy-Loading aller Pages: jede Page wird zu einem eigenen Code-Chunk,
 * der erst beim Navigieren geladen wird. Reduziert das initiale Bundle.
 *
 * React.lazy braucht einen Default-Export — unsere Pages exportieren
 * benannte Symbole. Der `.then(...)`-Adapter mappt jeweils auf default.
 */
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const ProductDetail = lazy(() => import('./pages/ProductDetail').then((m) => ({ default: m.ProductDetail })))
const Simulate = lazy(() => import('./pages/Simulate').then((m) => ({ default: m.Simulate })))
const PricingCalculator = lazy(() =>
  import('./pages/PricingCalculator').then((m) => ({ default: m.PricingCalculator })),
)
const ScenarioCompare = lazy(() =>
  import('./pages/ScenarioCompare').then((m) => ({ default: m.ScenarioCompare })),
)
const Tornado = lazy(() => import('./pages/Tornado').then((m) => ({ default: m.Tornado })))
const Imports = lazy(() => import('./pages/Imports').then((m) => ({ default: m.Imports })))
const TopProfit = lazy(() => import('./pages/TopProfit').then((m) => ({ default: m.TopProfit })))
const Gruppe = lazy(() => import('./pages/Gruppe').then((m) => ({ default: m.Gruppe })))
const Foundation = lazy(() => import('./pages/Foundation').then((m) => ({ default: m.Foundation })))
const ChannelFamilies = lazy(() =>
  import('./pages/ChannelFamilies').then((m) => ({ default: m.ChannelFamilies }))
)
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })))
const Channels = lazy(() => import('./pages/Channels').then((m) => ({ default: m.Channels })))
const GuV = lazy(() => import('./pages/GuV').then((m) => ({ default: m.GuV })))
const Tiles = lazy(() => import('./pages/Tiles').then((m) => ({ default: m.Tiles })))
const Chart = lazy(() => import('./pages/Chart').then((m) => ({ default: m.Chart })))
const DataQuality = lazy(() => import('./pages/DataQuality').then((m) => ({ default: m.DataQuality })))
const DataQualityEks = lazy(() =>
  import('./pages/DataQualityEks').then((m) => ({ default: m.DataQualityEks }))
)
const DataQualitySalesWithoutEk = lazy(() =>
  import('./pages/DataQualitySalesWithoutEk').then((m) => ({
    default: m.DataQualitySalesWithoutEk,
  }))
)
const Returns = lazy(() => import('./pages/Returns').then((m) => ({ default: m.Returns })))
const DailyOverview = lazy(() =>
  import('./pages/DailyOverview').then((m) => ({ default: m.DailyOverview })),
)
const ReturnsWhatIf = lazy(() =>
  import('./pages/ReturnsWhatIf').then((m) => ({ default: m.ReturnsWhatIf })),
)
const SettingsOperatingCosts = lazy(() =>
  import('./pages/SettingsOperatingCosts').then((m) => ({ default: m.SettingsOperatingCosts })),
)
const SettingsSuppliers = lazy(() =>
  import('./pages/SettingsSuppliers').then((m) => ({ default: m.SettingsSuppliers })),
)
const SettingsConstants = lazy(() =>
  import('./pages/SettingsConstants').then((m) => ({ default: m.SettingsConstants })),
)
const SettingsSyncs = lazy(() =>
  import('./pages/SettingsSyncs').then((m) => ({ default: m.SettingsSyncs })),
)
const SourcingList = lazy(() => import('./pages/SourcingList').then((m) => ({ default: m.SourcingList })))
const SourcingDetail = lazy(() =>
  import('./pages/SourcingDetail').then((m) => ({ default: m.SourcingDetail })),
)
const SourcingQuotes = lazy(() =>
  import('./pages/SourcingQuotes').then((m) => ({ default: m.SourcingQuotes })),
)
const SourcingComparison = lazy(() =>
  import('./pages/SourcingComparison').then((m) => ({ default: m.SourcingComparison })),
)
const Cashflow = lazy(() => import('./pages/Cashflow').then((m) => ({ default: m.Cashflow })))
const Reorder = lazy(() => import('./pages/Reorder').then((m) => ({ default: m.Reorder })))
const Inventory = lazy(() => import('./pages/Inventory').then((m) => ({ default: m.Inventory })))
const Inbox = lazy(() => import('./pages/Inbox').then((m) => ({ default: m.Inbox })))
const Manual = lazy(() => import('./pages/Manual').then((m) => ({ default: m.Manual })))

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

/** Fallback während ein Page-Chunk geladen wird. Bewusst dezent — die Page
 *  rendert dann ihre eigene Skeleton-Logik für Daten-Loading. */
function PageLoader() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 24,
        minHeight: '100vh',
        background: 'var(--paper)',
      }}
    >
      <Skeleton height={28} width="40%" />
      <Skeleton height={14} width="60%" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 8 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} height={88} />
        ))}
      </div>
      <Skeleton height={320} />
    </div>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <Toaster />
      <Router>
        <AuthProvider>
          <CommandPalette />
          <Suspense fallback={<PageLoader />}>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tiles"
              element={
                <ProtectedRoute>
                  <Tiles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <ProductDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/simulate"
              element={
                <ProtectedRoute>
                  <Simulate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pricing"
              element={
                <ProtectedRoute>
                  <PricingCalculator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scenarios/compare"
              element={
                <ProtectedRoute>
                  <ScenarioCompare />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scenarios/tornado"
              element={
                <ProtectedRoute>
                  <Tornado />
                </ProtectedRoute>
              }
            />
            <Route
              path="/imports"
              element={
                <ProtectedRoute>
                  <Imports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/top-profit"
              element={
                <ProtectedRoute>
                  <TopProfit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/gruppe"
              element={
                <ProtectedRoute>
                  <Gruppe />
                </ProtectedRoute>
              }
            />
            <Route
              path="/foundation"
              element={
                <ProtectedRoute>
                  <Foundation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/channels"
              element={
                <ProtectedRoute>
                  <Channels />
                </ProtectedRoute>
              }
            />
            <Route
              path="/channels/families"
              element={
                <ProtectedRoute>
                  <ChannelFamilies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/guv"
              element={
                <ProtectedRoute>
                  <GuV />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tiles"
              element={
                <ProtectedRoute>
                  <Tiles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chart"
              element={
                <ProtectedRoute>
                  <Chart />
                </ProtectedRoute>
              }
            />
            <Route
              path="/data-quality"
              element={
                <ProtectedRoute>
                  <DataQuality />
                </ProtectedRoute>
              }
            />
            <Route
              path="/data-quality/eks"
              element={
                <ProtectedRoute>
                  <DataQualityEks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/data-quality/sales-without-ek"
              element={
                <ProtectedRoute>
                  <DataQualitySalesWithoutEk />
                </ProtectedRoute>
              }
            />
            <Route
              path="/daily"
              element={
                <ProtectedRoute>
                  <DailyOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/returns"
              element={
                <ProtectedRoute>
                  <Returns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/returns-what-if"
              element={
                <ProtectedRoute>
                  <ReturnsWhatIf />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sourcing"
              element={
                <ProtectedRoute>
                  <SourcingList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sourcing/quotes"
              element={
                <ProtectedRoute>
                  <SourcingQuotes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sourcing/comparison"
              element={
                <ProtectedRoute>
                  <SourcingComparison />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sourcing/:id"
              element={
                <ProtectedRoute>
                  <SourcingDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cashflow"
              element={
                <ProtectedRoute>
                  <Cashflow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reorder"
              element={
                <ProtectedRoute>
                  <Reorder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute>
                  <Inventory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inbox"
              element={
                <ProtectedRoute>
                  <Inbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manual"
              element={
                <ProtectedRoute>
                  <Manual />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/operating-costs"
              element={
                <ProtectedRoute>
                  <SettingsOperatingCosts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/suppliers"
              element={
                <ProtectedRoute>
                  <SettingsSuppliers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/syncs"
              element={
                <ProtectedRoute>
                  <SettingsSyncs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/constants"
              element={
                <ProtectedRoute>
                  <SettingsConstants />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  )
}
